import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCircle2, RotateCcw, CheckCheck, Inbox, MessageSquare, Link2, UserCheck, ArrowRightLeft, CalendarClock } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30000;

const TYPE_META = {
  task_completed: {
    Icon: CheckCircle2,
    tint: 'bg-emerald-100 text-emerald-600',
    label: 'Task completed',
  },
  task_rework_requested: {
    Icon: RotateCcw,
    tint: 'bg-amber-100 text-amber-600',
    label: 'Rework requested',
  },
  task_remarks_updated: {
    Icon: MessageSquare,
    tint: 'bg-violet-100 text-violet-600',
    label: 'New remarks',
  },
  task_output_added: {
    Icon: Link2,
    tint: 'bg-sky-100 text-sky-600',
    label: 'Output added',
  },
  task_assignee_changed: {
    Icon: UserCheck,
    tint: 'bg-rose-100 text-rose-600',
    label: 'Assignee updated',
  },
  task_status_changed: {
    Icon: ArrowRightLeft,
    tint: 'bg-slate-100 text-slate-600',
    label: 'Status updated',
  },
  task_date_updated: {
    Icon: CalendarClock,
    tint: 'bg-orange-100 text-orange-600',
    label: 'Date updated',
  },
};

const relativeTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  const refresh = async () => {
    try {
      const [listRes, countRes] = await Promise.all([
        fetch(`${API_URL}/api/notifications?limit=30`, { credentials: 'include' }),
        fetch(`${API_URL}/api/notifications/unread-count`, { credentials: 'include' }),
      ]);
      if (!mounted.current) return;
      if (listRes.ok) {
        const data = await listRes.json();
        if (mounted.current) setItems(Array.isArray(data) ? data : []);
      }
      if (countRes.ok) {
        const data = await countRes.json();
        if (mounted.current) setUnread(Number(data?.count || 0));
      }
    } catch {
      /* silent — bell is a passive widget */
    }
  };

  useEffect(() => {
    mounted.current = true;
    // Defer to next tick so the linter doesn't trace setState into this effect's synchronous body.
    const initial = setTimeout(refresh, 0);
    const id = setInterval(refresh, POLL_MS);
    return () => {
      mounted.current = false;
      clearTimeout(initial);
      clearInterval(id);
    };
  }, []);

  // Refresh once when popover opens
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(refresh, 0);
    return () => clearTimeout(t);
  }, [open]);

  const markRead = async (id) => {
    try {
      await fetch(`${API_URL}/api/notifications/${id}/mark-read`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      /* non-blocking */
    }
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/mark-all-read`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        await refresh();
        toast.success('All notifications marked as read');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async (n) => {
    if (!n.is_read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnread((c) => Math.max(0, c - 1));
      await markRead(n.id);
    }
    if (n.task_display_id || n.task_id) {
      navigate(`/tasks?taskId=${encodeURIComponent(n.task_display_id || n.task_id)}`);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="notif-fab fixed bottom-24 right-6 z-40 group"
          data-testid="notifications-bell-btn"
          aria-label="Notifications"
        >
          <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-xl shadow-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-500/40 hover:scale-105 active:scale-95 transition-all duration-200 flex items-center justify-center">
            <Bell
              className="h-6 w-6"
              style={{
                animation: unread > 0 ? 'wiggle 0.5s ease-in-out infinite' : 'none',
                transformOrigin: 'top center',
              }}
            />
            {unread > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center border-2 border-white shadow-md tabular-nums"
                data-testid="notifications-unread-badge"
              >
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </div>
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap bg-slate-900 text-white text-xs font-medium px-2.5 py-1.5 rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity shadow-lg">
            Notifications
            {unread > 0 && <span className="ml-1.5 text-emerald-300">· {unread} new</span>}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" side="top" className="w-96 p-0" sideOffset={12}>
        <div className="px-4 py-3 border-b border-border bg-gradient-to-r from-emerald-500/10 to-teal-500/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-emerald-600" />
            <h3 className="font-semibold text-sm">Notifications</h3>
            {unread > 0 && (
              <span className="ml-1 inline-flex items-center justify-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500 text-white">
                {unread} new
              </span>
            )}
          </div>
          {unread > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              disabled={loading}
              className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="notifications-mark-all-read"
            >
              <CheckCheck className="h-3 w-3" />
              Mark all read
            </button>
          )}
        </div>

        <div className="max-h-[420px] overflow-y-auto" data-testid="notifications-list">
          {items.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <div className="w-12 h-12 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
                <Inbox className="h-5 w-5 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-700">You&apos;re all caught up</p>
              <p className="text-xs text-slate-400 mt-0.5">New activity will show up here</p>
            </div>
          ) : (
            items.map((n, idx) => {
              const meta = TYPE_META[n.type] || {
                Icon: Bell,
                tint: 'bg-slate-100 text-slate-600',
                label: 'Notification',
              };
              const Icon = meta.Icon;
              return (
                <button
                  type="button"
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left px-4 py-3 transition-colors ${
                    idx !== items.length - 1 ? 'border-b border-border/50' : ''
                  } ${n.is_read ? 'bg-white hover:bg-slate-50' : 'bg-emerald-50/40 hover:bg-emerald-50/70'}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.tint}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm leading-tight ${n.is_read ? 'text-slate-700' : 'text-slate-900 font-semibold'}`}>
                          {n.title}
                        </p>
                        {!n.is_read && (
                          <span className="mt-1.5 w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                        )}
                      </div>
                      {n.message && (
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{n.message}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                          {meta.label}
                        </span>
                        <span className="text-[10px] text-slate-400">·</span>
                        <span className="text-[10px] text-slate-400">{relativeTime(n.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
